import React, { useMemo } from 'react';
import { Picker } from '../../teacher/components/shared/UI';
import {
  STYLE_OPTIONS, SIZE_OPTIONS, LEVEL_OPTIONS, TYPE_OPTIONS, FREESTYLE_STYLES,
  START_POSITION_OPTIONS, SIZE_DANCER_RANGES, DANCER_ROLES, CHOREOGRAPHER_ROLES,
} from '../entryOptions';

/**
 * The entry-creation form fields — Type/Title/Style/Size/Level/start
 * position, dancer & choreographer Pickers, the K-Pop original-choreographer
 * credit, prop description, and the acknowledgement checkbox — shared
 * between the teacher's own "+ New entry" form (EntryLibrary.js's
 * EntryEditor, which wraps this with its own Music upload section and
 * Save/Cancel/Delete buttons) and the public choreographer submission page
 * (ChoreographerSubmission.js), so the freestyle-toggle, K-Pop, size-mismatch
 * and recommended-level logic only lives in one place.
 *
 * Deliberately has no network calls and no teacher-only imports (no
 * sheetsClient, no TeacherDataContext) so it's safe to import from the
 * public registration bundle. `onHelp`, if provided, wires up the Level/
 * Size/Style "?" buttons — the teacher form passes it (backed by a
 * teacher-authenticated definitions fetch); the public form omits it and the
 * buttons simply don't render, since that endpoint requires a teacher session.
 */
export default function EntryFormFields({ form, setForm, dancers, groups, allowedTypes, onHelp }) {
  const typeOptions = allowedTypes || TYPE_OPTIONS;
  const isFreestyle = form.type === 'Freestyle';
  const isCreativeCollab = form.type === 'Creative Collaboration';

  const [sizeMin, sizeMax] = SIZE_DANCER_RANGES[form.size] || [];
  const sizeMismatch = !isFreestyle && form.dancerIds.length > 0 && sizeMin != null &&
    (form.dancerIds.length < sizeMin || form.dancerIds.length > sizeMax);
  const sizeRequirementText = sizeMin === sizeMax
    ? `exactly ${sizeMin} dancer${sizeMin === 1 ? '' : 's'}`
    : sizeMax === Infinity
      ? `${sizeMin}+ dancers`
      : `${sizeMin}–${sizeMax} dancers`;

  const recommendedLevel = getRecommendedLevelIfBelow(form, dancers);
  const levelBelowRecommended = recommendedLevel !== null;

  const dancerNames = (ids) => dancers.filter((d) => ids.includes(d.id)).map((d) => `${d.firstName} ${d.lastName}`).join(', ');

  const dancerOptions = useMemo(() => dancers.filter((d) => DANCER_ROLES.includes(d.role)), [dancers]);
  const choreographerOptions = useMemo(() => dancers.filter((d) => CHOREOGRAPHER_ROLES.includes(d.role)), [dancers]);

  const handleTypeChange = (newType) => {
    if (newType === 'Freestyle') {
      setForm(f => ({ ...f, type: newType, style: 'Contemporary', size: 'Solo', title: '', dancerIds: [], choreographerIds: [], showNonAttending: false, nonAttendingChoreographers: '', props: false, propDescription: '', startPosition: '' }));
    } else {
      setForm(f => ({ ...f, type: newType }));
    }
  };

  const toggleId = (field, id) => {
    setForm((f) => {
      const set = new Set(f[field]);
      set.has(id) ? set.delete(id) : set.add(id);
      return { ...f, [field]: Array.from(set) };
    });
  };

  // ids is the currently-visible (search-filtered) option list from the
  // Picker; shouldClear tells us whether it's already fully selected (in
  // which case the button un-selects just those, rather than everything).
  const selectAllIds = (field) => (ids, shouldClear) => {
    setForm((f) => {
      const set = new Set(f[field]);
      ids.forEach((id) => (shouldClear ? set.delete(id) : set.add(id)));
      return { ...f, [field]: Array.from(set) };
    });
  };

  const toggleFreestyleDancer = (id) => {
    const dancer = dancers.find(d => d.id === id);
    if (!dancer) return;
    if (form.dancerIds.includes(id)) {
      setForm(f => ({ ...f, dancerIds: [], choreographerIds: [], title: '' }));
    } else {
      setForm(f => ({ ...f, dancerIds: [id], choreographerIds: [id], title: `${dancer.firstName} ${dancer.lastName[0]}.` }));
    }
  };

  const loadGroup = (groupId) => {
    const group = (groups || []).find((g) => g.id === groupId);
    if (!group) return;
    setForm((f) => ({ ...f, dancerIds: Array.from(new Set([...f.dancerIds, ...group.dancerIds])) }));
  };

  // Only pulls in the group's members who are actually eligible to be a
  // choreographer (registered as 'Choreographer' or 'Dancer and
  // Choreographer') — a group is just a saved set of dancers, not
  // role-tagged, so a group built for the dancer picker may still contain
  // dancer-only members that shouldn't land in this field.
  const loadGroupForChoreographers = (groupId) => {
    const group = (groups || []).find((g) => g.id === groupId);
    if (!group) return;
    const eligibleIds = group.dancerIds.filter((id) => {
      const dancer = dancers.find((d) => d.id === id);
      return dancer && CHOREOGRAPHER_ROLES.includes(dancer.role);
    });
    setForm((f) => ({ ...f, choreographerIds: Array.from(new Set([...f.choreographerIds, ...eligibleIds])) }));
  };

  const [dancerPickerOpen, setDancerPickerOpen] = React.useState(false);
  const [choreoPickerOpen, setChoreoPickerOpen] = React.useState(false);

  // "Other (not attending)" — free-text student choreographer names for
  // people with no roster row. Unchecking clears the names so nothing hidden
  // gets saved.
  const showNonAttending = isNonAttendingShown(form);
  const toggleNonAttending = () => setForm((f) => {
    const on = !isNonAttendingShown(f);
    return { ...f, showNonAttending: on, nonAttendingChoreographers: on ? (f.nonAttendingChoreographers || '') : '' };
  });
  const choreographerPickerValue = [
    dancerNames(form.choreographerIds),
    showNonAttending && form.nonAttendingChoreographers?.trim() ? `${form.nonAttendingChoreographers.trim()} (not attending)` : '',
  ].filter(Boolean).join(', ');

  return (
    <>
      <FormRow label="Entry Type" noMargin>
        <select style={inputStyle} value={form.type} onChange={(e) => handleTypeChange(e.target.value)}>
          {typeOptions.map((s) => <option key={s}>{s}</option>)}
        </select>
      </FormRow>

      <FormRow label="Entry title" required>
        {isFreestyle ? (
          <input style={{ ...inputStyle, background: '#F5F5F5', color: '#888' }} value={form.title} readOnly placeholder="Auto-filled from dancer name" />
        ) : (
          <input style={inputStyle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        )}
      </FormRow>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        {!isCreativeCollab && (
          <FormRow label="Style" noMargin required onHelp={onHelp ? () => onHelp('style') : undefined}>
            <select
              style={inputStyle}
              value={form.style}
              onChange={(e) => setForm({ ...form, style: e.target.value, ...(e.target.value !== 'K-Pop' ? { originalChoreographers: '' } : {}) })}
            >
              {(isFreestyle ? FREESTYLE_STYLES : STYLE_OPTIONS).map((s) => <option key={s}>{s}</option>)}
            </select>
            {form.style === 'K-Pop' && (
              <div style={{ fontSize: 12, fontStyle: 'italic', color: '#C0392B', marginTop: 5 }}>
                Reminder: K-Pop entries are not adjudicated.
              </div>
            )}
          </FormRow>
        )}
        {!isFreestyle && (
          <FormRow label="Size" noMargin required onHelp={onHelp ? () => onHelp('size') : undefined}>
            <select style={inputStyle} value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })}>
              {SIZE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
            {sizeMismatch && (
              <div style={{ fontSize: 12, color: '#C0392B', marginTop: 5 }}>
                {form.size} requires {sizeRequirementText} — {form.dancerIds.length} selected.
              </div>
            )}
          </FormRow>
        )}
        {isFreestyle && (
          <FormRow label="Size" noMargin required>
            <div style={{ ...inputStyle, background: '#F5F5F5', color: '#888', display: 'flex', alignItems: 'center' }}>Solo</div>
          </FormRow>
        )}
        {!isFreestyle && (
          <FormRow label="Level" noMargin required onHelp={onHelp ? () => onHelp('level') : undefined}>
            <select style={inputStyle} value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}>
              {LEVEL_OPTIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
            {levelBelowRecommended && (
              <div style={{ fontSize: 12, color: '#C0392B', marginTop: 5 }}>
                This entry's level is below the average experience level of its participants — it must be {recommendedLevel} or higher.
              </div>
            )}
          </FormRow>
        )}

        {!isFreestyle && (
          <FormRow label="Does your entry start on stage or off stage?" required>
            <select style={inputStyle} value={form.startPosition || ''} onChange={(e) => setForm({ ...form, startPosition: e.target.value })}>
              <option value="" disabled>Select one…</option>
              {START_POSITION_OPTIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </FormRow>
        )}

      </div>

      <FormRow label="Participating dancer(s)" required>
        <span style={{ fontSize: 13, fontStyle: 'italic', color: '#666' }}>Dancer and choreographer names are pulled from your registered roster.</span>
        <Picker
          open={dancerPickerOpen}
          onToggle={() => setDancerPickerOpen((o) => !o)}
          value={dancerNames(form.dancerIds)}
          placeholder={isFreestyle ? 'Select one dancer...' : 'Select dancers from roster...'}
          groups={isFreestyle ? undefined : groups}
          onLoadGroup={isFreestyle ? undefined : loadGroup}
          options={isFreestyle ? dancers : dancerOptions}
          selectedIds={form.dancerIds}
          onToggleId={isFreestyle ? toggleFreestyleDancer : (id) => toggleId('dancerIds', id)}
          onSelectAll={isFreestyle ? undefined : selectAllIds('dancerIds')}
        />
      </FormRow>

      {isFreestyle ? (
        form.choreographerIds.length > 0 && (
          <FormRow label="Choreographer(s)">
            <div style={{ ...inputStyle, background: '#F5F5F5', color: '#888', display: 'flex', alignItems: 'center', gap: 8 }}>
              {dancerNames(form.choreographerIds)}
              <span style={{ fontSize: 12 }}>(same as dancer)</span>
            </div>
          </FormRow>
        )
      ) : (
        <FormRow label="Choreographer(s)" required>
          <Picker
            open={choreoPickerOpen}
            onToggle={() => setChoreoPickerOpen((o) => !o)}
            value={choreographerPickerValue}
            placeholder="Select choreographers from roster..."
            groups={groups}
            onLoadGroup={loadGroupForChoreographers}
            options={choreographerOptions}
            selectedIds={form.choreographerIds}
            onToggleId={(id) => toggleId('choreographerIds', id)}
            onSelectAll={selectAllIds('choreographerIds')}
            extraOption={{ label: 'Other (not attending)', checked: showNonAttending, onToggle: toggleNonAttending }}
          />
          {showNonAttending && (
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 13, color: '#666', marginBottom: 5, display: 'block' }}>
                Student choreographer(s) not attending<span style={{ color: '#C0392B', marginLeft: 2 }}>*</span>
              </label>
              <input
                style={inputStyle}
                value={form.nonAttendingChoreographers || ''}
                onChange={(e) => setForm({ ...form, nonAttendingChoreographers: e.target.value })}
                placeholder="e.g. Jane Doe, Sam Lee"
                maxLength={500}
              />
            </div>
          )}
        </FormRow>
      )}

      {form.style === 'K-Pop' && (
        <FormRow label="Original choreographer(s)" required>
          <span style={{ fontSize: 13, fontStyle: 'italic', color: '#666' }}>
            K-Pop entries recreate existing choreography — enter the name(s) of the original choreographer(s) being credited.
          </span>
          <input
            style={inputStyle}
            value={form.originalChoreographers || ''}
            onChange={(e) => setForm({ ...form, originalChoreographers: e.target.value })}
            placeholder="e.g. Choreography by..."
          />
        </FormRow>
      )}

      {!isFreestyle && (
        <FormRow label="I have a prop">
          <input
            type="checkbox"
            checked={form.props}
            onChange={(e) => setForm({ ...form, props: e.target.checked })}
            style={{ marginTop: 2, flexShrink: 0 }}
          />
        </FormRow>
      )}
      {!isFreestyle && form.props && (
        <FormRow label="Prop/Set Piece Description">
          <textarea
            style={{ ...inputStyle, height: 80, resize: 'vertical' }}
            value={form.propDescription || ''}
            onChange={(e) => setForm({ ...form, propDescription: e.target.value })}
            placeholder="Describe your prop or set piece here. If it is large, please provide approximate sizing."
          />
          <span style={{ fontSize: 13, fontStyle: 'italic', color: '#666' }}>
            ** Props must be removed from the Theatre by 5 pm on Friday.
          </span>
        </FormRow>
      )}

      {!form.id && (
        <FormRow label="Acknowledgement" required>
          <div style={{ display: 'flex', alignItems: 'flex-center', gap: 8 }}>
            <input
              type="checkbox"
              checked={form.acknowledgement}
              onChange={(e) => setForm({ ...form, acknowledgement: e.target.checked })}
              style={{ marginTop: 2, flexShrink: 0 }}
            />
            <span style={{ fontSize: 13, color: '#666' }}>
              I/we verify that all choreography in this dance entry is original, composed by a student/students.
              The music used is appropriate in content, cultural meaning and lyrics. Costumes must be original and
              should be simple and effective with a focus on the artistic intent of the student choreographer.
              Student choreographers should seek to collaborate in the costume selection process with student dancers
              and receive their consent in wearing them. Costumes should include the use of appropriate undergarments
              as an integrated element that maintains the artistic integrity of the work. Please come to the live event
              with lots of pins, tape and sewing kits to remove any and/or all risk of costume malfunctions. Furthermore,
              costumes borrowed from private dance studios are not permitted.
            </span>
          </div>
        </FormRow>
      )}
    </>
  );
}

/**
 * Returns the recommended level name if the entry's registered level sits
 * below it, otherwise null. Exported so the Level dropdown warning and the
 * teacher form's submit-time confirmation share the same rule.
 *
 * Recommended level is the average of the selected dancers' experience
 * levels (as positions in LEVEL_OPTIONS), floored to a named level — e.g.
 * two Intermediate 2s and one Advanced 1 averages to index 2.33, which
 * floors to Intermediate 2 (index 2); an average of 2.66 also floors to
 * Intermediate 2, not up to Advanced 1 — the group only "graduates" to the
 * next named level once its average actually reaches it. The entry is
 * flagged when its own registered level sits below that floored level, so
 * e.g. with an average of 2.33 or 2.66, only Novice or Intermediate 1
 * would warn — Intermediate 2 itself never does.
 */
export function getRecommendedLevelIfBelow(form, dancers) {
  // Saved entries (Entry Library list) may lack `type` and store Freestyle
  // as the level instead, so check both.
  if (form.type === 'Freestyle' || form.level === 'Freestyle') return null;
  const selectedDancerLevelIndexes = (form.dancerIds || [])
    .map((id) => LEVEL_OPTIONS.indexOf(dancers.find((d) => d.id === id)?.experienceLevel))
    .filter((i) => i !== -1);
  if (selectedDancerLevelIndexes.length === 0) return null;
  const recommendedLevelIndex = Math.floor(selectedDancerLevelIndexes.reduce((sum, i) => sum + i, 0) / selectedDancerLevelIndexes.length);
  return LEVEL_OPTIONS.indexOf(form.level) < recommendedLevelIndex ? LEVEL_OPTIONS[recommendedLevelIndex] : null;
}

// Whether the "Other (not attending)" box is ticked. A saved entry loaded
// for editing has no showNonAttending flag, so it's ticked if it already
// has names.
function isNonAttendingShown(form) {
  return form.showNonAttending ?? !!form.nonAttendingChoreographers;
}

// The non-attending names to save — '' when the box is unticked or empty.
// Both entry forms use this for their payload and their "has a
// choreographer" validation.
export function getNonAttendingChoreographers(form) {
  if (form.type === 'Freestyle' || !isNonAttendingShown(form)) return '';
  return (form.nonAttendingChoreographers || '').trim();
}

export function FormRow({ label, children, noMargin, required, onHelp }) {
  return (
    <div style={{ marginBottom: noMargin ? 0 : 14 }}>
      <label style={{ fontSize: 13, color: '#666', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 5 }}>
        {label}{required && <span style={{ color: '#C0392B', marginLeft: 2 }}>*</span>}
        {onHelp && (
          <button
            type="button"
            onClick={onHelp}
            aria-label={`What does ${label} mean?`}
            title={`What does ${label} mean?`}
            style={{
              width: 16, height: 16, borderRadius: '50%', border: '1px solid #B5B5B5', background: '#fff',
              color: '#666', fontSize: 10, lineHeight: '14px', padding: 0, cursor: 'pointer', flexShrink: 0,
            }}
          >
            ?
          </button>
        )}
      </label>
      {children}
    </div>
  );
}

export const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #D5D5D5', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' };
